import type {
  ClientAdProductType,
  PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import type { DisplayingRegionLevel } from '../../../../types/DisplayingRegionLevel';
import { Box, Card, Heading, Paragraph, Table } from '@zillow/constellation-10';
import { mapPackageTypeName } from '../../../../utils/mapPackageTypeName';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { getRegionIdKeyFromRegionLevel } from '../../../../utils/getRegionIdKeyFromRegionLevel';
import { AdSpendMapSidebarEmptyState } from './AdSpendMapSidebarEmptyState';

type AdSpendMapSidebarProps = {
  propertiesToRender: Array<
    PortfolioAnalyticsPropertyResponseDto & {
      packageType: ClientAdProductType;
    }
  >;
  finalDisplayMode: DisplayingRegionLevel;
  isLoading?: boolean;
  regionName?: string;
};

export const AdSpendMapSidebar = ({
  propertiesToRender,
  finalDisplayMode,
  isLoading = false,
}: AdSpendMapSidebarProps) => {
  const { regionsMapping } = useMainLayout();
  const groupingKey = getRegionIdKeyFromRegionLevel(finalDisplayMode);
  const packagesCountMap = new Map<ClientAdProductType, number>();

  const groupedProperties = propertiesToRender.reduce(
    (acc, property) => {
      const propertyCount = packagesCountMap.get(property.packageType);

      if (propertyCount !== undefined) {
        packagesCountMap.set(property.packageType, propertyCount + 1);
      } else {
        packagesCountMap.set(property.packageType, 1);
      }

      const key = property.propertyDetails[groupingKey];
      if (key && !acc[key]) {
        acc[key] = [];
        acc[key].push(property as unknown as PortfolioAnalyticsPropertyResponseDto);
      } else if (acc[key]) {
        acc[key].push(property as unknown as PortfolioAnalyticsPropertyResponseDto);
      }
      return acc;
    },
    {} as Record<string, Array<PortfolioAnalyticsPropertyResponseDto>>,
  );

  const allPackages: Array<ClientAdProductType> = Array.from(packagesCountMap.keys());
  const hasNoProperties = Object.keys(groupedProperties).length === 0;

  if (hasNoProperties && !isLoading) {
    return <AdSpendMapSidebarEmptyState />;
  }

  return (
    <Box css={{ padding: '400' }}>
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '200', marginBlockEnd: '400' }}>
        {allPackages.map((packageType) => (
          <Card elevated={false} key={packageType}>
            <Paragraph textStyle="body-sm-bold">{mapPackageTypeName(packageType)}</Paragraph>
            <Heading level={5}>{packagesCountMap.get(packageType)}</Heading>
          </Card>
        ))}
      </Box>
      <Table.Root aria-label="ad spend table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Advertising markets</Table.HeaderCell>
            <Table.HeaderCell>Ad spend</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {Object.keys(groupedProperties).map((regionId) => {
            const adSpendTotal = groupedProperties[regionId].reduce((acc, property) => {
              return acc + property.aggregateStats.totalCost;
            }, 0);

            const regionName = regionsMapping[Number(regionId)];

            return (
              <Table.Row key={regionId}>
                <Table.Cell>{regionName}</Table.Cell>
                <Table.Cell>{formatCurrency(adSpendTotal)}</Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};
